import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { shortTitle, shortDescription } from "@/utils/metaLength";
import { getLangFromUrl, setLangInUrl, useHreflang, updateCanonical } from "@/utils/seoLang";
import axios from "axios";
import { CITIES } from "@/cityConfig";
import { getCurrencyByCode, convertPrice } from "@/currencyConfig";
import { t } from "@/translations";
import { trackConversion } from "@/utils/analytics";
import { HelpCircle, Plus } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ProductsSection from "@/components/ProductsSection";
import ProductPreview from "@/components/ProductPreview";
import { istanbulHotelContent, ISTANBUL_HOTEL_PRODUCT_IDS } from "@/data/istanbulHotelContent";

const DEFAULT_LANGS = ["ru", "en"];

const API = "/api";

function Table({ table }) {
  if (!table) return null;
  return (
    <div className="overflow-x-auto my-4 rounded-xl border border-gray-100">
      <table className="w-full text-sm text-left" data-testid="hotel-table">
        <thead className="bg-gray-50 text-gray-700">
          <tr>{table.head.map((h, i) => (<th key={i} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>))}</tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r} className="border-t border-gray-100">
              {row.map((c, ci) => (<td key={ci} className="px-4 py-3 text-gray-600 align-top">{c}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FAQ({ items, title }) {
  const [open, setOpen] = useState(null);
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({ "@type": "Question", name: it.q, acceptedAnswer: { "@type": "Answer", text: it.a } })),
  };
  return (
    <section className="py-12 sm:py-16 bg-white" data-testid="hotel-faq">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-1.5 rounded-full mb-4">
            <HelpCircle className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-white">FAQ</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "Playfair Display, serif" }}>{title}</h2>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {items.map((it, idx) => (
            <div key={idx} className={`border-b border-gray-100 last:border-b-0 ${open === idx ? "bg-pink-50/30" : ""}`}>
              <button
                onClick={() => setOpen(open === idx ? null : idx)}
                className="w-full text-left px-5 sm:px-6 py-4 flex items-center justify-between gap-4"
                data-testid={`hotel-faq-toggle-${idx}`}
              >
                <span className="font-semibold text-gray-900">{it.q}</span>
                <Plus className={`w-4 h-4 text-pink-500 shrink-0 transition-transform ${open === idx ? "rotate-45" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-200 ${open === idx ? "max-h-[600px] pb-4" : "max-h-0"}`}>
                <p className="px-5 sm:px-6 text-sm text-gray-600 leading-relaxed">{it.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HotelDeliveryPage({
  citySlug = "istanbul",
  path = "/istanbul/hotel-flower-delivery",
  content = istanbulHotelContent,
  productIds = ISTANBUL_HOTEL_PRODUCT_IDS,
  productNames = null,
} = {}) {
  const CITY_SLUG = citySlug;
  const PATH = path;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const city = CITIES[CITY_SLUG];
  const availableLanguages = city?.languages || DEFAULT_LANGS;

  const [lang, setLangState] = useState(() => getLangFromUrl(searchParams, availableLanguages));
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(city?.currency || "USD");
  const [previewProduct, setPreviewProduct] = useState(null);

  const setLang = (newLang) => {
    setLangState(newLang);
    setLangInUrl(newLang, searchParams, setSearchParams);
  };

  useHreflang(PATH, availableLanguages);

  const c = content[lang] || content.ru;

  useEffect(() => {
    document.title = shortTitle(c.metaTitle);
    const setMeta = (sel, val) => { const el = document.querySelector(sel); if (el && val) el.setAttribute("content", val); };
    setMeta('meta[name="description"]', shortDescription(c.metaDescription));
    setMeta('meta[property="og:title"]', c.metaTitle);
    setMeta('meta[property="og:description"]', c.metaDescription);
    setMeta('meta[property="og:url"]', `https://yuccaflower.com${PATH}`);
    setMeta('meta[name="twitter:title"]', c.metaTitle);
    setMeta('meta[name="twitter:description"]', c.metaDescription);
    updateCanonical(PATH, availableLanguages);
    document.documentElement.lang = lang;
  }, [c, lang, availableLanguages, PATH]);

  const fetchData = useCallback(async () => {
    try {
      const [pr, st, rt] = await Promise.all([
        axios.get(`${API}/products?city=${CITY_SLUG}`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/exchange-rates`),
      ]);
      setProducts(pr.data);
      setSettings(st.data);
      setExchangeRates(rt.data.rates);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const curated = useMemo(() => {
    if (productNames && productNames.length) {
      const byName = Object.fromEntries(products.map((p) => [p.name, p]));
      return productNames.map((n) => byName[n]).filter(Boolean);
    }
    const byId = Object.fromEntries(products.map((p) => [p.id, p]));
    return productIds.map((id) => byId[id]).filter(Boolean);
  }, [products, productIds, productNames]);

  const baseCurrency = city.currency;
  const displayCurrency = getCurrencyByCode(selectedCurrency || baseCurrency);
  const cp = (price) => convertPrice(price, baseCurrency, displayCurrency.code, exchangeRates);

  const openWhatsApp = (product = null) => {
    const phone = settings?.whatsapp?.replace(/[^0-9]/g, "") || "905316224378";
    let message;
    if (product) {
      const prodName = lang === "tr" && product.name_tr ? product.name_tr : (lang === "en" && product.name_en ? product.name_en : product.name);
      message = lang === "en"
        ? `Hello! I'd like to send "${prodName}" to a hotel in ${c.breadcrumb.city}.`
        : `Здравствуйте! Хочу отправить "${prodName}" в отель, ${c.waCity || c.breadcrumb.city}.`;
    } else {
      message = lang === "en"
        ? `Hello! I'd like to send flowers to a hotel in ${c.breadcrumb.city}.`
        : `Здравствуйте! Хочу отправить цветы в отель, ${c.waCity || c.breadcrumb.city}.`;
    }
    trackConversion("whatsapp", product?.name || `${CITY_SLUG}-hotel`);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const scrollToCatalog = () => {
    const el = document.getElementById("hotel-catalog");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const langHref = (href) => href;

  return (
    <>
      <Header
        settings={settings}
        currentCity={city}
        onCityChange={(slug) => navigate(`/${slug}`)}
        selectedCurrency={selectedCurrency || baseCurrency}
        onCurrencyChange={setSelectedCurrency}
        lang={lang}
        onLangChange={setLang}
        t={(key, vars) => t(key, lang, vars)}
        availableLanguages={availableLanguages}
        solid
      />

      <main className="md:pt-20">
        {/* Breadcrumb */}
        <nav className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 text-sm text-gray-500" aria-label="Breadcrumb" data-testid="hotel-breadcrumb">
          <ol className="flex flex-wrap items-center gap-2">
            <li><a href="/" className="hover:text-pink-600">{c.breadcrumb.home}</a></li>
            <li>/</li>
            <li><a href={`/${CITY_SLUG}${lang !== "ru" ? `?lang=${lang}` : ""}`} className="hover:text-pink-600">{c.breadcrumb.city}</a></li>
            <li>/</li>
            <li className="text-gray-700 font-medium">{c.breadcrumb.current}</li>
          </ol>
        </nav>

        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-10" data-testid="hotel-hero">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "Playfair Display, serif" }} data-testid="hotel-h1">
            {c.h1}
          </h1>
          <p className="text-gray-700 leading-relaxed mb-5 text-base">{c.heroLead}</p>
          <ul className="grid sm:grid-cols-2 gap-2 mb-6">
            {c.heroBullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-pink-500 mt-0.5">✦</span><span>{b}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={scrollToCatalog}
            data-testid="hotel-cta-hero"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold px-7 py-3 rounded-full hover:opacity-90 transition-opacity"
          >
            {c.cta}
          </button>
        </section>

        {/* Catalog */}
        <div id="hotel-catalog" className="pt-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "Playfair Display, serif" }}>{c.catalogHeading}</h2>
            <p className="text-gray-500 text-sm mt-2">{c.catalogNote}</p>
          </div>
          <ProductsSection
            products={curated}
            onOrderClick={openWhatsApp}
            onImageClick={setPreviewProduct}
            cityName={c.breadcrumb.city}
            currencySymbol={displayCurrency.symbol}
            convertPrice={cp}
            lang={lang}
            t={(key, vars) => t(key, lang, vars)}
          />
          <div className="text-center my-6">
            <a href={`/${CITY_SLUG}${lang !== "ru" ? `?lang=${lang}` : ""}`} data-testid="hotel-full-catalog"
              className="inline-block px-6 py-3 rounded-full border border-emerald-600 text-emerald-700 font-medium hover:bg-emerald-50 transition-colors">
              {c.fullCatalog}
            </a>
          </div>
        </div>

        {/* Content sections */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          {c.sections.map((s, i) => (
            <section key={i} className="mb-10" data-testid={`hotel-section-${i}`}>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Playfair Display, serif" }}>{s.h2}</h2>
              <p className="text-gray-900 font-medium mb-3">{s.answer}</p>
              {s.paras && s.paras.map((p, pi) => (<p key={pi} className="text-gray-700 leading-relaxed mb-3">{p}</p>))}
              {s.steps && (
                <ol className="list-decimal pl-5 space-y-2 text-gray-700 mb-2">
                  {s.steps.map((st, si) => (<li key={si}>{st}</li>))}
                </ol>
              )}
              {s.bullets && (
                <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-2">
                  {s.bullets.map((bt, bi) => (<li key={bi}>{bt}</li>))}
                </ul>
              )}
              {s.table && <Table table={s.table} />}
              {s.note && <p className="text-gray-500 text-sm mt-3 italic">{s.note}</p>}
            </section>
          ))}
        </div>

        {/* At a glance */}
        <section className="bg-gray-50 border-t border-gray-100" data-testid="hotel-glance">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: "Playfair Display, serif" }}>{c.glanceTitle}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {c.glance.map((g, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col">
                  <span className="text-xs uppercase tracking-wide text-pink-600 font-semibold mb-1">{g.label}</span>
                  <span className="text-sm text-gray-800">{g.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FAQ items={c.faq} title={c.faqTitle} />

        {/* Related links */}
        <section className="bg-white border-t border-gray-100" data-testid="hotel-related">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5" style={{ fontFamily: "Playfair Display, serif" }}>{c.relatedTitle}</h2>
            <div className="flex flex-wrap gap-3">
              {c.related.map((r, i) => (
                <a key={i} href={langHref(r.href)} data-testid={`hotel-related-${i}`}
                  className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors border border-pink-200 rounded-full px-4 py-2 hover:bg-pink-50">
                  {r.label}<span className="ml-1">→</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-gradient-to-r from-pink-500 to-rose-500 text-white" data-testid="hotel-cta-bottom">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 text-center">
            <button onClick={scrollToCatalog}
              className="inline-flex items-center gap-2 bg-white text-pink-600 font-semibold px-8 py-3 rounded-full hover:bg-pink-50 transition-colors">
              {c.cta}
            </button>
          </div>
        </section>
      </main>

      <Footer settings={settings} city={city} lang={lang} t={(key, vars) => t(key, lang, vars)} />

      {/* BreadcrumbList schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: c.breadcrumb.home, item: "https://yuccaflower.com/" },
          { "@type": "ListItem", position: 2, name: c.breadcrumb.city, item: `https://yuccaflower.com/${CITY_SLUG}` },
          { "@type": "ListItem", position: 3, name: c.breadcrumb.current, item: `https://yuccaflower.com${PATH}` },
        ],
      }) }} />

      <FloatingWhatsApp onClick={() => openWhatsApp()} />

      {previewProduct && (
        <ProductPreview
          product={previewProduct}
          onClose={() => setPreviewProduct(null)}
          onOrderClick={openWhatsApp}
          currencySymbol={displayCurrency.symbol}
          convertPrice={cp}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
        />
      )}
    </>
  );
}
